import { useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { useViewQuota } from "@/hooks/queries/useViewQuota";
import { useBetaInformation } from "@/hooks/queries/useBetaInformation";
import type { BillingState } from "@/types/domain";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

dayjs.locale("es");

// Por debajo de este porcentaje de saldo el chip pasa a ámbar y ofrece mejorar.
const LOW_QUOTA_RATIO = 0.3;

type QuotaState = "healthy" | "low" | "empty";

// Presentación de cada estado. El CTA solo aparece cuando queda poco o nada:
// en estado sano el chip es informativo y no interrumpe.
const STATE_STYLES: Record<
  QuotaState,
  {
    ring: string;
    track: string;
    text: string;
    chip: string;
    bar: string;
  }
> = {
  healthy: {
    ring: "text-primary",
    track: "text-primary/15",
    text: "text-slate-700 dark:text-slate-200",
    chip: "bg-primary/10 text-primary",
    bar: "bg-primary",
  },
  low: {
    ring: "text-amber-500",
    track: "text-amber-500/20",
    text: "text-amber-700 dark:text-amber-400",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400",
    bar: "bg-amber-500",
  },
  empty: {
    ring: "text-red-400",
    track: "text-red-400/20",
    text: "text-red-700 dark:text-red-400",
    chip: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400",
    bar: "bg-red-400",
  },
};

function resolveState(remaining: number, limit: number): QuotaState {
  if (remaining <= 0) return "empty";
  if (limit > 0 && remaining / limit < LOW_QUOTA_RATIO) return "low";
  return "healthy";
}

// ---- AVISO DE COBRO ----

type NoticeTone = "info" | "warn" | "danger";

type BillingNotice = {
  tone: NoticeTone;
  title: string;
  detail: string;
};

const NOTICE_STYLES: Record<NoticeTone, { box: string; icon: string }> = {
  info: {
    box: "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900",
    icon: "text-slate-500 dark:text-slate-400",
  },
  warn: {
    box: "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/40",
    icon: "text-amber-600 dark:text-amber-400",
  },
  danger: {
    box: "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/40",
    icon: "text-red-600 dark:text-red-400",
  },
};

function inDays(days: number | null): string {
  if (days == null) return "pronto";
  if (days === 0) return "hoy";
  if (days === 1) return "mañana";
  return `en ${days} días`;
}

/**
 * Traduce el estado de cobro al aviso que se muestra dentro del popover. Renovarse
 * no es una alerta (recarga las vistas del mes); un cobro rechazado o un plan que
 * termina sí piden actuar.
 */
function resolveBillingNotice(
  state: BillingState,
  daysRemaining: number | null,
): BillingNotice | null {
  switch (state) {
    case "RENEWING":
      return {
        tone: "info",
        title: `Se renueva ${inDays(daysRemaining)}`,
        detail: "El cobro es automático y te recarga las vistas del mes.",
      };
    case "PAYMENT_FAILED":
      return {
        tone: "danger",
        title: "No pudimos cobrar tu tarjeta",
        detail: "Actualiza tu medio de pago para no perder el acceso.",
      };
    case "EXPIRED":
      return {
        tone: "danger",
        title: "Tu plan venció",
        detail: "Renueva tu suscripción para seguir viendo licitaciones.",
      };
    case "CANCELLED":
    case "ENDING":
      return {
        tone: "warn",
        title: `Vence ${inDays(daysRemaining)}`,
        detail: "No se renovará. Mantienes el acceso hasta esa fecha.",
      };
    default:
      return null;
  }
}

function BillingNoticeBox({ notice }: { notice: BillingNotice }) {
  const styles = NOTICE_STYLES[notice.tone];
  const Icon = notice.tone === "info" ? RefreshCw : AlertTriangle;

  return (
    <div className={cn("mt-3 flex gap-2 rounded-lg border p-2.5", styles.box)}>
      <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", styles.icon)} />
      <div className="space-y-0.5">
        <p className="text-xs font-bold leading-tight">{notice.title}</p>
        <p className="text-xs leading-snug text-muted-foreground">
          {notice.detail}
        </p>
      </div>
    </div>
  );
}

/** Anillo de progreso: el arco representa el saldo que queda. */
function QuotaRing({
  ratio,
  ringClass,
  trackClass,
}: {
  ratio: number;
  ringClass: string;
  trackClass: string;
}) {
  const radius = 8;
  const circumference = 2 * Math.PI * radius;

  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5 shrink-0 -rotate-90">
      <circle
        cx="10"
        cy="10"
        r={radius}
        fill="none"
        strokeWidth="3.5"
        stroke="currentColor"
        className={trackClass}
      />
      {ratio > 0 && (
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          strokeWidth="3.5"
          strokeLinecap="round"
          stroke="currentColor"
          strokeDasharray={`${circumference * ratio} ${circumference}`}
          className={ringClass}
        />
      )}
    </svg>
  );
}

/**
 * Chip de plan y saldo de vistas de la barra superior. Es el único control de
 * suscripción del header: el saldo se lee de un vistazo y todo lo demás (consumo
 * del mes, renovación, cobros rechazados) vive dentro del popover.
 */
export function ViewQuotaBadge() {
  const navigate = useNavigate();
  const { data: quota } = useViewQuota();
  const { data: info } = useBetaInformation();
  const [isOpen, setIsOpen] = useState(false);

  if (!quota) return null;

  const planName = quota.planName ?? info?.plan?.name ?? null;
  const notice = info?.plan
    ? resolveBillingNotice(info.plan.state, info.plan.daysRemaining)
    : null;
  // Solo lo accionable se anuncia en el header con un punto; la renovación se
  // descubre al abrir el popover.
  const needsAttention = notice != null && notice.tone !== "info";

  const limit = quota.limit ?? 0;
  const state = quota.unlimited
    ? "healthy"
    : resolveState(quota.remaining, limit);
  const styles = STATE_STYLES[state];

  // Un ajuste manual del admin puede dejar más saldo que el límite del plan. En
  // ese caso el denominador no significa nada: se muestra solo el saldo y se
  // avisa a cuánto volverá al cambiar de mes.
  const hasExtraViews = quota.remaining > limit;
  const remainingRatio = quota.unlimited
    ? 1
    : hasExtraViews
      ? 1
      : limit > 0
        ? quota.remaining / limit
        : 0;
  const remainingLabel = hasExtraViews
    ? String(quota.remaining)
    : `${quota.remaining}/${limit}`;
  // La fecha la manda el backend: cada usuario recarga en su propio aniversario y
  // el cálculo va en TZ de Perú, no en la del navegador. Sin fecha = las vistas
  // del plan no se recargan nunca (son de por vida).
  const renewalDate = quota.resetsAt
    ? dayjs(quota.resetsAt).format("D [de] MMMM")
    : null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex items-center gap-1.5 rounded-full border border-slate-100 bg-card py-1.5 pl-2 pr-2.5 shadow-sm transition-colors hover:bg-accent data-[state=open]:border-blue-500 sm:gap-2 sm:pl-2.5 sm:pr-3 dark:border-slate-800 dark:bg-slate-900"
        >
          <QuotaRing
            ratio={remainingRatio}
            ringClass={styles.ring}
            trackClass={styles.track}
          />
          {/* En móvil no cabe el denominador: basta el saldo, el anillo ya da la
              proporción y el detalle está a un toque. */}
          <span
            className={cn(
              "whitespace-nowrap text-xs font-bold tabular-nums",
              styles.text,
            )}
          >
            <span className="sm:hidden">
              {quota.unlimited ? "∞" : quota.remaining}
            </span>
            <span className="hidden sm:inline">
              {quota.unlimited ? "Ilimitado" : remainingLabel}
            </span>
          </span>
          {planName && (
            <span
              className={cn(
                "whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                styles.chip,
              )}
            >
              {planName}
            </span>
          )}
          {needsAttention && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-red-500 dark:border-slate-900" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[300px] p-4">
        {planName && (
          <span
            className={cn(
              "inline-block rounded-md px-2 py-1 text-xs font-bold uppercase tracking-wider",
              styles.chip,
            )}
          >
            {planName}
          </span>
        )}

        {quota.unlimited ? (
          <p className="mt-3 text-base font-bold text-slate-700 dark:text-slate-200">
            Vistas de licitación ilimitadas
          </p>
        ) : (
          <>
            <p className={cn("mt-3 text-base font-bold", styles.text)}>
              {quota.remaining === 0
                ? "Te quedaste sin vistas de licitación"
                : `Te ${quota.remaining === 1 ? "queda" : "quedan"} ${quota.remaining} vista${quota.remaining === 1 ? "" : "s"} de licitación`}
            </p>

            <div className="mt-4">
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-300">
                  Vistas disponibles
                </span>
                <span className="font-bold tabular-nums">{remainingLabel}</span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    "h-full rounded-full transition-all",
                    styles.bar,
                  )}
                  style={{ width: `${remainingRatio * 100}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {!renewalDate
                  ? `Son ${limit} vistas para toda tu cuenta: no se recargan.`
                  : hasExtraViews
                    ? `El ${renewalDate} tu saldo vuelve a ${limit}, el límite de tu plan.`
                    : `El ${renewalDate} se te recargan tus ${limit} vistas.`}
              </p>
            </div>
          </>
        )}

        {notice && <BillingNoticeBox notice={notice} />}

        <Button
          className="mt-4 w-full font-bold"
          variant={needsAttention ? "destructive" : "default"}
          onClick={() => {
            setIsOpen(false);
            navigate("/settings/plan");
          }}
        >
          {needsAttention ? "Revisar mi plan" : "Ver planes"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
